import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type FactualCreditFormValues = {
  appKey: string;
  username: string;
  password: string;
};

interface EditFactualCreditIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditFactualCreditIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditFactualCreditIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let appKey = '';
  let username = '';
  let password = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    appKey = data.appKey || '';
    username = data.username || '';
    password = data.password || '';
  }

  const validationSchema = yup.object().shape({
    appKey: yup.string().required('App Key is required'),
    username: yup.string().required('Username is required'),
    password: yup.string().required('Password is required'),
  });

  const { control, handleSubmit, formState } = useForm<FactualCreditFormValues>({
    defaultValues: {
      appKey: appKey,
      username: username,
      password: password,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: FactualCreditFormValues) => {
    console.log('Form validated successfully:', data);

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          vendor: IntegrationVendor.Factual,
          environment: environment,
          integrationData: JSON.stringify({
            appKey: data.appKey,
            username: data.username,
            password: data.password,
          }),
        };
      } else {
        settings = {
          name: 'Factual Credit Integration',
          vendor: IntegrationVendor.Factual,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            appKey: data.appKey,
            username: data.username,
            password: data.password,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Factual Credit Settings' : 'Factual Credit Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="appKey"
            label="App Key *"
            placeholder="Enter your Factual App Key"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="username"
            label="Username *"
            placeholder="Enter your Factual Username"
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="password"
            label="Password *"
            placeholder="Enter your Factual Password"
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
